
import { Save, AlertCircle, Plus, X } from "lucide-react";
import { useState } from "react";
import { axiosInstance } from "../../../utility/apiInstance";
import toast from "react-hot-toast";

const s: Record<string, React.CSSProperties> = {
  root: { paddingTop: "4px" },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  colHeaders: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 60px",
    padding: "0 44px 0 12px",
    marginBottom: "6px",
    minWidth: "560px",
  },
  colHeader: {
    fontSize: "10.5px",
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "0 5px",
  },

  rowWrap: { position: "relative", marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 60px",
    background: "#fafafa",
    border: "1px solid #ececec",
    borderRadius: "10px",
    padding: "10px 12px",
    alignItems: "start",
    minWidth: "560px",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  rowCardError: { borderColor: "#fca5a5", background: "#fffafa" },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  input: {
    border: "1px solid #e2e2e2",
    borderRadius: "6px",
    padding: "6px 8px",
    fontSize: "11.5px",
    fontFamily: "inherit",
    color: "#374151",
    background: "#fff",
    outline: "none",
    width: "100%",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputError: { borderColor: "#ef4444" },

  errText: {
    color: "#ef4444",
    fontSize: "10.5px",
    marginTop: "3px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },

  rowActionBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "7px",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.15s",
    marginTop: "1px",
  },

  addRowBtn: {
    background: "none",
    border: "1px dashed #d1d5db",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    color: "#9ca3af",
    fontFamily: "inherit",
    cursor: "pointer",
    width: "100%",
    marginTop: "4px",
    transition: "border-color 0.15s, color 0.15s",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "12px 0 0",
    borderTop: "1px solid #f0f0f0",
    marginTop: "8px",
  },
  saveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#F8B71F",
    border: "none",
    borderRadius: "7px",
    padding: "7px 16px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#1a1a1a",
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 1px 4px rgba(248,183,31,0.35)",
    transition: "background 0.15s",
  },
};

function ErrorHint({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

function SectionHeader({ label, meta }: { label: string; meta?: string }) {
  return (
    <div style={s.sectionHeader}>
      <div style={s.sectionTitle}>
        <div style={s.sectionAccent} />
        <span style={s.sectionLabel}>{label}</span>
      </div>
      {meta && <span style={s.rowCount}>{meta}</span>}
    </div>
  );
}

export const ContainerOffline = ({
  containerData,
  setContainerData,
  oceanTrackingNumber,
  getListOfflineContainer,
  trackingData,
}: any) => {
  const [milestones, setMilestones] = useState([]) as any;
  const [containerErrors, setContainerErrors] = useState<any[]>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const handleChange = (index: number, key: string, value: string) => {
    const updated = [...containerData];
    updated[index][key] = value;
    setContainerData(updated);
  };

  const handleAddContainer = () => {
    setContainerData((prev: any) => [
      ...prev,
      {
        _id: Date.now().toString(),
        container_number: "",
        type: "",
      },
    ]);
  };

  const handleDeleteContainer = (id: string) => {
    setContainerData((prev: any) => prev.filter((v: any) => v._id !== id));
    deleteContainerOceanOffline(id);
  };

  const isCT = trackingData?.type === "CT";
  const isAddDisabled = isCT && containerData.length >= 1;

  const saveContainerOceanOffline = async () => {
    let errors: any[] = [];
    let hasError = false;

    containerData.forEach((cont: any, index: number) => {
      errors[index] = {};

      if (!cont.container_number || cont.container_number.trim() === "") {
        errors[index].container_number = "Container Number is required";
        hasError = true;
      }

      if (!cont.type || cont.type.trim() === "") {
        errors[index].type = "Type is required";
        hasError = true;
      }
    });

    setContainerErrors(errors);
    if (hasError) return;

    try {
      const payloadData = containerData?.map((item: any) => ({
        ...item,
        oceanTrackingNumber: oceanTrackingNumber,
      }));

      await axiosInstance.post(`/api/v1/ocean/add-offilne-container`, {
        containerData: payloadData,
      });

      getListOfflineContainer();
      toast.success("container added successfully!");
    } catch (error) {
      toast.error("Failed to Add container!");
      return;
    }
  };

  const fetchMilestoneOfAwbNumber = async () => {
    if (!oceanTrackingNumber) return null;
    try {
      const { data } = await axiosInstance.get(
        `/api/v1/offline-ocean-milestone/${oceanTrackingNumber}`
      );
      setMilestones(data?.data?.allfinalMileStone);
    } catch (error) {
      return [];
    }
  };

  const deleteContainerOceanOffline = async (id: string) => {
    if (!checkIsDisabledContainer()) return;
    try {
      await axiosInstance.delete(`api/v1/ocean/offilne-container/delete-container/${id}`);
      getListOfflineContainer();
      toast.success("container deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete container!");
      return;
    }
  };

  const checkIsDisabledContainer = () => {
    return milestones?.length > 0;
  };

  const hasRowError = (index: number) =>
    !!containerErrors[index] && Object.values(containerErrors[index]).some(Boolean);

  return (
    <div style={s.root}>
      <SectionHeader
        label="Container Info"
        meta={`${containerData.length} container${containerData.length !== 1 ? "s" : ""}${
          isCT ? " · CT mode" : ""
        }`}
      />

      <div style={{ overflowX: "auto" }}>
        <div style={s.colHeaders}>
          <span style={s.colHeader}>Container Number</span>
          <span style={s.colHeader}>Type</span>
          <span style={s.colHeader}>Action</span>
        </div>

        {containerData.map((cont: any, index: number) => {
          const hovered = hoveredRow === cont._id;
          const rowStyle: React.CSSProperties = {
            ...s.rowCard,
            ...(hasRowError(index) ? s.rowCardError : {}),
            ...(hovered ? s.rowCardHovered : {}),
          };

          return (
            <div
              key={cont._id}
              style={s.rowWrap}
              onMouseEnter={() => setHoveredRow(cont._id)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <div style={rowStyle}>
                <div style={s.cell}>
                  <input
                    type="text"
                    style={{
                      ...s.input,
                      ...(containerErrors[index]?.container_number ? s.inputError : {}),
                    }}
                    placeholder="Container Number"
                    value={cont.container_number ?? ""}
                    onChange={(e) => {
                      handleChange(index, "container_number", e.target.value.toUpperCase());
                      const err = [...containerErrors];
                      if (err[index]) {
                        err[index].container_number = "";
                        setContainerErrors(err);
                      }
                    }}
                  />
                  <ErrorHint message={containerErrors[index]?.container_number} />
                </div>

                <div style={s.cell}>
                  <input
                    type="text"
                    style={{ ...s.input, ...(containerErrors[index]?.type ? s.inputError : {}) }}
                    placeholder="Type"
                    value={cont.type ?? ""}
                    onChange={(e) => {
                      handleChange(index, "type", e.target.value.toUpperCase());
                      const err = [...containerErrors];
                      if (err[index]) {
                        err[index].type = "";
                        setContainerErrors(err);
                      }
                    }}
                  />
                  <ErrorHint message={containerErrors[index]?.type} />
                </div>

                <div style={s.cell}>
                  {index === 0 && !checkIsDisabledContainer() && !isAddDisabled ? (
                    <button
                      style={{ ...s.rowActionBtn, background: "#dcfce7", color: "#16a34a" }}
                      onClick={handleAddContainer}
                      title="Add container"
                    >
                      <Plus size={14} />
                    </button>
                  ) : index !== 0 && !checkIsDisabledContainer() ? (
                    <button
                      style={{ ...s.rowActionBtn, background: "#fee2e2", color: "#dc2626" }}
                      onClick={() => handleDeleteContainer(cont._id!)}
                      title="Delete container"
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!isAddDisabled && (
        <button
          style={s.addRowBtn}
          onClick={handleAddContainer}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#F8B71F";
            (e.currentTarget as HTMLButtonElement).style.color = "#374151";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db";
            (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af";
          }}
        >
          + Add container
        </button>
      )}

      <div style={s.footer}>
        <button style={s.saveBtn} onClick={saveContainerOceanOffline}>
          <Save size={13} />
          Save Container
        </button>
      </div>
    </div>
  );
};